import { motion } from 'framer-motion';
import { Bell, Check, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../../auth/Auth';
import { subscribeAfterUserConsent } from '../../webPush/webPushClient';
import { showToast } from '../../utils/showToast';

const MODAL_ID = 'push-notification-subscribe';

interface PushNotificationSubscribeModalProps {
  onClose: () => void;
}

const benefits = [
  'Find out when your payment is delivered to your landlord',
  'Never miss the 7-day window to claim your free rent',
] as const;

export function PushNotificationSubscribeModal({ onClose }: PushNotificationSubscribeModalProps) {
  const { auth, currentUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const userId = currentUser?.user?.userId;

  const onNotNow = () => {

    onClose();
  };

  const onAllow = async () => {
    const token = auth?.data?.token;
    if (userId == null || !token) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const { ok, denied } = await subscribeAfterUserConsent(token, userId);
      if (ok) {
        showToast({ message: 'Notifications enabled', type: 'success' });
        onClose();
      } else if (denied) {
        showToast({
          message:
            'Notifications are blocked for this site. You can enable them in your browser settings.',
          type: 'error',
        });
        onClose();
      } else {
        showToast({
          message: 'Could not enable notifications. Try again later.',
          type: 'error',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className='relative flex w-full max-w-[min(92vw,400px)] flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl'
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      role='dialog'
      aria-labelledby={`${MODAL_ID}-title`}
      aria-describedby={`${MODAL_ID}-desc`}
    >
      <button
        type='button'
        className='absolute right-3 top-3 z-[1] rounded-full p-2 text-[var(--color-text-3)] transition-colors hover:bg-[var(--color-page)] hover:text-[var(--color-text)]'
        aria-label='Close'
        onClick={onClose}
      >
        <X size={20} strokeWidth={2} />
      </button>

      <div className='flex flex-col items-center px-6 pb-2 pt-10 text-center sm:px-8 sm:pt-11'>
        <div
          className='mb-5 flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-[var(--color-blue-tint)] ring-1 ring-[var(--color-border)]'
          aria-hidden
        >
          <Bell className='h-6 w-6 text-[var(--color-blue)]' strokeWidth={2} />
        </div>
        <h2
          id={`${MODAL_ID}-title`}
          className='text-[1.125rem] font-semibold leading-snug tracking-tight text-[var(--color-text)] sm:text-[1.25rem]'
        >
          Enable notifications
        </h2>
        <p
          id={`${MODAL_ID}-desc`}
          className='mt-2 max-w-[20rem] text-[13px] leading-relaxed text-[var(--color-text-2)] sm:text-[14px]'
        >
          Get timely updates about your rent, rewards, and account. You can change this anytime in
          your browser settings.
        </p>

        <ul className='mt-6 w-full max-w-[18.5rem] space-y-2.5 text-left'>
          {benefits.map((line) => (
            <li key={line} className='flex gap-2.5 text-[13px] leading-snug text-[var(--color-text-2)]'>
              <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-green-tint)]'>
                <Check className='h-3 w-3 text-[var(--color-green)]' strokeWidth={3} aria-hidden />
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className='mt-2.5 w-full max-w-[18.5rem] text-left text-[12px] leading-snug text-[var(--color-text-3)]'>
          Plus, find out about new rewards and offers.
        </p>
      </div>

      <div className='mt-4 flex flex-col gap-2 border-t border-[var(--color-border)] bg-[var(--color-page)] px-5 py-5 sm:px-6'>
        <button
          type='button'
          className='btn-gold flex h-[48px] w-full items-center justify-center rounded-[10px] px-6 text-[13px] font-medium'
          disabled={busy}
          onClick={() => void onAllow()}
        >
          {busy ? 'Enabling…' : 'Allow notifications'}
        </button>
        <button
          type='button'
          className='btn-ghost flex h-[44px] w-full items-center justify-center rounded-[10px] px-6 text-[13px]'
          disabled={busy}
          onClick={onNotNow}
        >
          Not now
        </button>
      </div>
    </motion.div>
  );
}
